/**
 * WorkspaceProvider — the single, shared "which workspace (agencyclient)
 * am I looking at" selection, lifted out of Kanban/Calendar/Social's own
 * duplicate copies.
 *
 * BUG-068: KanbanView, CalendarView, and SocialCalendarView each fetched
 * `/agencyclients/me` and tracked their own `selectedId` under three
 * different sessionStorage keys (`ccm:kanban:active_ws` /
 * `ccm:publication-calendar:active_ws` / `ccm:social-calendar:active_ws`)
 * — switching workspace on one view had no effect on the other two, and
 * each view's own `useEnsureActiveWorkspace(activeId)` call fought to set
 * the *real* backend/JWT active workspace to whatever it last remembered,
 * so navigating between views could silently flip the real active
 * workspace underneath you. This provider is the one shared piece of
 * state, mounted once in Shell.tsx around the routed views, so all three
 * consumers agree.
 *
 * `activeWorkspace`/`allWorkspaces` mirror the exact derivation every
 * consuming view already did locally (selectedId === 0 means "All
 * workspaces", fall back to workspaces[0] otherwise) — behavior is
 * unchanged, only its location moved.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useApiQuery } from "@/lib/api";
import {
  effectiveSelection,
  isValidSelection,
  resolveActiveWorkspace,
  type WorkspaceOption,
} from "@/lib/workspace-selection";

export type { WorkspaceOption };

interface WorkspaceContextValue {
  workspaces: WorkspaceOption[];
  isLoading: boolean;
  error: Error | null;
  /** 0 means "All workspaces". null means nothing selected yet. While a
   * workspace is pinned this is the pinned one. */
  selectedId: number | null;
  setSelectedId: (id: number) => void;
  /** BUG-137: the workspace an open piece belongs to, or null. While set it
   * is the workspace in force and the picker is read-only. */
  pinnedId: number | null;
  setPinnedId: (id: number | null) => void;
  /** Convenience derivation every consumer needs: the resolved workspace
   * object (falls back to the first workspace when selectedId isn't 0). */
  activeWorkspace: WorkspaceOption | undefined;
  allWorkspaces: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

const ACTIVE_WS_KEY = "ccm:active_ws";

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const {
    data: workspaces,
    isLoading,
    error,
  } = useApiQuery<WorkspaceOption[]>(["agencyclients", "me"], "/agencyclients/me");

  const [selectedId, setSelectedIdState] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(ACTIVE_WS_KEY);
    return raw ? Number(raw) : null;
  });

  const [pinnedId, setPinnedId] = useState<number | null>(null);

  function setSelectedId(id: number) {
    setSelectedIdState(id);
    try {
      window.sessionStorage.setItem(ACTIVE_WS_KEY, String(id));
    } catch {
      // ignore — sessionStorage unavailable (private mode, etc.)
    }
  }

  // Same auto-select-first-workspace behavior every prior copy of this
  // logic had: once the list loads, fall back off an invalid selection.
  useEffect(() => {
    if (workspaces && workspaces.length > 0 && !isValidSelection(workspaces, selectedId)) {
      setSelectedId(workspaces[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces]);

  const inForce = effectiveSelection(selectedId, pinnedId);
  const allWorkspaces = inForce === 0;
  const activeWorkspace = useMemo(
    () => resolveActiveWorkspace(workspaces ?? [], inForce),
    [workspaces, inForce]
  );

  const value: WorkspaceContextValue = {
    workspaces: workspaces ?? [],
    isLoading,
    error: error as Error | null,
    selectedId: inForce,
    setSelectedId,
    pinnedId,
    setPinnedId,
    activeWorkspace,
    allWorkspaces,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspaceContext(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error("useWorkspaceContext must be used inside <WorkspaceProvider>");
  }
  return ctx;
}
